/* The interface language for the Vue tier.
 *
 * WHICH language is decided by public/lang.js, not here, because the choice has
 * to be applied to <html lang> and <html dir> before first paint and the
 * standalone pages need the same switcher. This module only follows it.
 *
 * English is bundled; the other nine are fetched on demand. Ten catalogues at
 * roughly 8 KB each would be 80 KB carried by the ninety-odd per cent of
 * visitors who never touch the switcher.
 *
 * Keys are named for where a string lives and what it does, never for its
 * English text. `dropzone.headline`, not `"Drop your images here"` -- otherwise
 * every copy edit invalidates ten catalogues.
 */
import { createI18n } from "vue-i18n"
import en from "./locales/en.json"

export const SUPPORTED = ["en", "es", "de", "fr", "it", "ru", "zh", "ja", "ko", "ar"]

function initial() {
    // lang.js has already resolved page > stored > English and stamped <html>
    if (typeof window !== "undefined" && window.OIOLang) return window.OIOLang.current()
    return "en"
}

export const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: "en",
    fallbackLocale: "en",
    // A missing key should be visible in development and silent in production;
    // what it must never do is print "undefined" at a visitor.
    missingWarn: process.env.NODE_ENV !== "production",
    fallbackWarn: false,
    messages: { en },
})

const loaded = new Set(["en"])

export async function setLocale(code) {
    if (!SUPPORTED.includes(code)) code = "en"
    if (!loaded.has(code)) {
        try {
            const messages = await import(
                /* webpackChunkName: "locale-[request]" */ `./locales/${code}.json`)
            i18n.global.setLocaleMessage(code, messages.default || messages)
            loaded.add(code)
        } catch (err) {
            // A catalogue that will not load is not a reason to break the tool.
            // English is already there; stay on it.
            return
        }
    }
    i18n.global.locale.value = code
}

/* Called once at boot. Follows lang.js from then on, including a change made
 * from the switcher in a server-rendered masthead, which is outside the app. */
export function startLocale() {
    setLocale(initial())
    if (typeof window !== "undefined" && window.OIOLang) {
        window.OIOLang.onChange(setLocale)
    }
}
